import { useState, useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Grid,
  IconButton,
  Paper,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
} from '@mui/material'
import EuroIcon from '@mui/icons-material/Euro'

import GridHeader from '../components/GridHeader'
import TableCellTeam from '../components/table/TableCellTeam'
import ButtonRunDownload from '../components/ButtonRunDownload'
import SelectionBar from '../components/selection/SelectionBar'
import SortableTableHead from '../components/SortableTableHead'
import GridCircularProgress from '../components/GridCircularProgress'
import DialogSanzioneReferto from '../components/DialogSanzioneReferto'
import { config } from '../settings/configs'
import { colours } from '../settings/settings'
import { selectCalendariProvvedimentiSorted } from '../store/selectors'
import {
  useGetCalendariProvvedimentiQuery,
  useUpdateCalendarioSanzioneRefertoMutation,
  useGetImpostazioniAvanzateQuery,
  useGetIscrizioniQuery,
} from '../hooks/api-slice'
import {
  fromDateToString,
  fromStringsToDateTime,
  DAYS_TO_MILLISECONDS,
  filterByRitardo,
} from '../hooks/utils-functions'
import { setSelectedNumeriGara } from '../container/selection'

const AVANZ_FIELDS = [
  'GiorniRitardoRisultati',
  'GiorniRitardoReferti',
  'SquadraInvioRisultato',
  'SquadraInvioRisultatoFinali',
]

const isRitardo = x => x?.includes('ritardo')

const Provvedimenti = () => {
  const selectedCampionato = useSelector(
    state => state.selection.selectedCampionato,
  )
  const { data: iscrizioniLight } = useGetIscrizioniQuery(
    { campionato: selectedCampionato, iFilter: 'light' },
    { skip: !selectedCampionato },
  )
  const availableRitardi = useSelector(
    state => state.selection.availableRitardi,
  )
  const selectedRitardo = useSelector(state => state.selection.selectedRitardo)
  const availableProvvedimenti = useSelector(
    state => state.selection.availableProvvedimenti,
  )
  const selectedProvvedimento = useSelector(
    state => state.selection.selectedProvvedimento,
  )
  const availableArbitraggi = useSelector(
    state => state.selection.availableArbitraggi,
  )
  const selectedArbitraggio = useSelector(
    state => state.selection.selectedArbitraggio,
  )

  const { data: avanzate, isLoading: isLoadingF } =
    useGetImpostazioniAvanzateQuery(AVANZ_FIELDS)
  const ritRisultati = avanzate?.GiorniRitardoRisultati
  const ritReferti = avanzate?.GiorniRitardoReferti
  const squadraIR = avanzate?.SquadraInvioRisultato
  const squadraIRFinali = avanzate?.SquadraInvioRisultatoFinali

  const pArgs = { selectedCampionato, selectedProvvedimento }
  const { isFetching: isFetchingC } = useGetCalendariProvvedimentiQuery(pArgs, {
    skip: !(selectedCampionato && selectedProvvedimento),
  })
  const { isFetching: isFetchingI } = useGetIscrizioniQuery(
    { campionato: selectedCampionato, iFilter: 'nomi' },
    { skip: !selectedCampionato },
  )
  const partite = useSelector(state =>
    selectCalendariProvvedimentiSorted(state, pArgs),
  )

  const partite1 = isRitardo(selectedProvvedimento)
    ? filterByRitardo(partite, availableRitardi, selectedRitardo)
    : partite

  const filterByNeedsReferee = useCallback(
    x =>
      x?.filter(f =>
        selectedArbitraggio === availableArbitraggi?.[1]
          ? !!f.needsReferee || !!f.requiresReferee
          : selectedArbitraggio === availableArbitraggi?.[2]
            ? !f.needsReferee && !f.requiresReferee
            : true,
      ),
    [availableArbitraggi, selectedArbitraggio],
  )

  const partiteN = filterByNeedsReferee(partite1)

  const columns = [
    { label: 'Gara', sortable: true, sortingField: 'numeroGara' },
    { label: 'Data', sortable: false },
    { label: 'Squadra in casa', sortable: false },
    { label: 'Squadra in trasferta', sortable: false },
  ]

  const columnsRisultatiMancanti = [
    ...columns,
    { label: 'Sanzione', sortable: false },
  ]

  const columnsRisultatiRitardo = [
    ...columns,
    { label: 'Inviato', sortable: false },
    { label: 'Ritardo', sortable: true, sortingField: 'daysDelay' },
    { label: 'Sanzione', sortable: false },
  ]

  const columnsRefertiMancanti = [
    ...columns,
    { label: 'Sanzione', sortable: false },
  ]

  const columnsRefertiRitardo = [
    ...columns,
    { label: 'Omologa', sortable: true, sortingField: 'timeHomologation' },
    { label: 'Ritardo', sortable: true, sortingField: 'daysDelay' },
    { label: 'Sanzione', sortable: false },
  ]

  const columnsN = [
    columnsRisultatiMancanti,
    columnsRisultatiRitardo,
    columnsRefertiMancanti,
    columnsRefertiRitardo,
  ][availableProvvedimenti?.indexOf(selectedProvvedimento)]

  const [updateCalendarioSanzioneReferto] =
    useUpdateCalendarioSanzioneRefertoMutation()

  const dispatch = useDispatch()

  document.title = `${config?.name} - Provvedimenti`

  const [openEditSanzioneReferto, setOpenEditSanzioneReferto] = useState()
  const [editSanzioneReferto, setEditSanzioneReferto] = useState()

  const handleClickOpenEditSanzioneReferto = id => {
    setEditSanzioneReferto(partiteN?.find(i => i.id === id))
    setOpenEditSanzioneReferto(true)
  }

  const handleCloseEditSanzioneReferto = () => {
    setOpenEditSanzioneReferto()
    setEditSanzioneReferto()
  }

  useEffect(() => {
    if (availableProvvedimenti?.includes(selectedProvvedimento))
      dispatch(setSelectedNumeriGara(partiteN?.map(f => f.numeroGara)))
  }, [availableProvvedimenti, dispatch, partiteN, selectedProvvedimento])

  const updateSanzioneReferto = async (
    squadraCasaSanzionataPerReferto,
    sendMail,
  ) => {
    const body = { squadraCasaSanzionataPerReferto, sendMail }
    const result = await updateCalendarioSanzioneReferto({
      id: editSanzioneReferto?.id,
      ...body,
    })
    if (!result?.error) {
      handleCloseEditSanzioneReferto()
      setEditSanzioneReferto()
    }
  }

  const squadraRisultato = c =>
    (c.girone === 'finali' && squadraIRFinali) || squadraIR

  const isSanzionataRisultato = c => {
    const sR = squadraRisultato(c)
    return sR === 'casa'
      ? c.squadraCasaSanzionataPerRisultato
      : sR === 'trasferta'
        ? c.squadraTrasfertaSanzionataPerRisultato
        : sR === 'vincente'
          ? c.setVintiCasa > c.setVintiTrasferta
            ? c.squadraCasaSanzionataPerRisultato
            : c.setVintiTrasferta > c.setVintiCasa
              ? c.squadraTrasfertaSanzionataPerRisultato
              : false
          : false
  }

  const IconSanzione = (c, g) => {
    const sq = g === 'risultato' ? squadraRisultato(c) : 'casa'
    const isSanz =
      g === 'risultato'
        ? isSanzionataRisultato(c)
        : c.squadraCasaSanzionataPerReferto

    return (
      <Tooltip
        title={`Squadra ${
          (sq === 'casa' && 'di casa') ||
          (sq === 'trasferta' && 'in trasferta') ||
          sq
        } ${isSanz ? '' : 'non '}sanzionata`}
      >
        <EuroIcon
          style={{ color: isSanz ? colours.green : colours.greyVeryLight }}
        />
      </Tooltip>
    )
  }

  const SortableTableHeadN = () => (
    <SortableTableHead
      table={partiteN}
      columns={columnsN}
      tableName='provvedimenti'
    />
  )

  return (
    <Grid container paddingX={2} spacing={2} sx={{ width: '100%' }}>
      <GridHeader h2 text='Provvedimenti disciplinari' />
      <Grid container alignItems='center' sx={{ width: '100%' }}>
        <SelectionBar
          data={iscrizioniLight}
          yCampionato
          yProvvedimento
          yArbitraggio
          yRitardo={isRitardo(selectedProvvedimento)}
        />
        <Grid size={{ xs: 12, md: 'grow' }}>
          <ButtonRunDownload
            buttonText='Scarica Excel'
            algorithm='09_GenerateExcelProvvedimenti'
            type='XLSX'
            disabled={!selectedCampionato || !selectedProvvedimento}
          />
        </Grid>
      </Grid>
      {(!availableProvvedimenti?.includes(selectedProvvedimento) && (
        <Grid size={12}>
          <center>Selezionare un tipo di provvedimento.</center>
        </Grid>
      )) ||
        ((isFetchingC || isLoadingF || isFetchingI) && (
          <GridCircularProgress />
        )) ||
        (!partiteN?.length && <GridHeader text='Nulla da segnalare.' />) ||
        (selectedProvvedimento === availableProvvedimenti?.[0] && (
          <Grid container sx={{ width: '100%' }}>
            <p>
              <b>{partiteN?.length || 0} risultati </b>
              non sono ancora stati inviati.
              <br />
              Le gare evidenziate risalgono a più di {ritRisultati} giorni fa e
              saranno sanzionate una volta inviato il risultato
              {config?.features?.rendiconti &&
                ' e aggiornato il rendiconto della società'}
              .
            </p>
            <TableContainer component={Paper}>
              <Table size='small'>
                <SortableTableHeadN />
                <TableBody>
                  {partiteN?.map((c, index) => {
                    const mDate = fromStringsToDateTime(c.data, c.ora)
                    const nDate = new Date()
                    const isLate =
                      nDate - mDate > ritRisultati * DAYS_TO_MILLISECONDS

                    return (
                      <TableRow
                        key={index}
                        style={{
                          backgroundColor: isLate
                            ? colours.redError
                            : colours.white,
                        }}
                      >
                        <TableCell>{c.numeroGara}</TableCell>
                        <TableCell>{c.data}</TableCell>
                        <TableCellTeam id={c.squadraCasaID} />
                        <TableCellTeam id={c.squadraTrasfertaID} />
                        <TableCell>{IconSanzione(c, 'risultato')}</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )) ||
        (selectedProvvedimento === availableProvvedimenti[1] && (
          <Grid container sx={{ width: '100%' }}>
            <p>
              <b>{partiteN?.length || 0} risultati </b>
              sono stati inviati più di {ritRisultati} giorni dopo l'orario di
              inizio gara e sono da sanzionare.
              <br />
              Finora {partiteN?.filter(isSanzionataRisultato)?.length || 0} sono
              stati sanzionati.
            </p>
            <TableContainer component={Paper}>
              <Table size='small'>
                <SortableTableHeadN />
                <TableBody>
                  {partiteN?.map((c, index) => (
                    <TableRow key={index}>
                      <TableCell>{c.numeroGara}</TableCell>
                      <TableCell>{c.data}</TableCell>
                      <TableCellTeam id={c.squadraCasaID} />
                      <TableCellTeam id={c.squadraTrasfertaID} />
                      <TableCell>
                        {fromDateToString(
                          new Date(
                            c.timeResultInsertedByCasa ||
                              c.timeResultInsertedByTrasferta ||
                              c.timeResultUpdatedByAdmin,
                          ),
                          true,
                        )}
                      </TableCell>
                      <TableCell>{Math.floor(c.daysDelay)} giorni</TableCell>
                      <TableCell>{IconSanzione(c, 'risultato')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )) ||
        (selectedProvvedimento === availableProvvedimenti?.[2] && (
          <Grid container sx={{ width: '100%' }}>
            <p>
              <b>{partiteN?.length || 0} referti </b>
              non sono ancora stati consegnati.
              <br />
              {ritReferti != null &&
                `Le gare evidenziate risalgono a più di ${ritReferti} giorni fa e
                sono da sanzionare.`}
            </p>
            <TableContainer component={Paper}>
              <Table size='small'>
                <SortableTableHeadN />
                <TableBody>
                  {partiteN?.map((c, index) => {
                    const mDate = fromStringsToDateTime(c.data, c.ora)
                    const nDate = new Date()
                    const isLate =
                      nDate - mDate > ritReferti * DAYS_TO_MILLISECONDS

                    return (
                      <TableRow
                        key={index}
                        style={{
                          backgroundColor: isLate
                            ? colours.redError
                            : colours.white,
                        }}
                      >
                        <TableCell>{c.numeroGara}</TableCell>
                        <TableCell>{c.data}</TableCell>
                        <TableCellTeam id={c.squadraCasaID} />
                        <TableCellTeam id={c.squadraTrasfertaID} />
                        <TableCell>
                          <IconButton
                            size='small'
                            onClick={() =>
                              handleClickOpenEditSanzioneReferto(c.id)
                            }
                          >
                            {IconSanzione(c, 'referto')}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )) ||
        (selectedProvvedimento === availableProvvedimenti?.[3] && (
          <Grid container sx={{ width: '100%' }}>
            {ritReferti != null && (
              <p>
                <b>{partiteN?.length || 0} referti </b>
                sono stati inviati più di {ritReferti} giorni dopo l'orario di
                inizio gara e sono da sanzionare. Finora{' '}
                {partiteN?.filter(
                  r => r.squadraCasaSanzionataPerReferto === true,
                )?.length || 0}{' '}
                sono stati sanzionati.
              </p>
            )}
            <TableContainer component={Paper}>
              <Table size='small'>
                <SortableTableHeadN />
                <TableBody>
                  {partiteN?.map((c, index) => (
                    <TableRow key={index}>
                      <TableCell>{c.numeroGara}</TableCell>
                      <TableCell>{c.data}</TableCell>
                      <TableCellTeam id={c.squadraCasaID} />
                      <TableCellTeam id={c.squadraTrasfertaID} />
                      <TableCell>
                        {fromDateToString(new Date(c.timeHomologation), true)}
                      </TableCell>
                      <TableCell>{Math.floor(c.daysDelay)} giorni</TableCell>
                      <TableCell>
                        <IconButton
                          size='small'
                          onClick={() =>
                            handleClickOpenEditSanzioneReferto(c.id)
                          }
                        >
                          {IconSanzione(c, 'referto')}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        ))}
      <DialogSanzioneReferto
        open={openEditSanzioneReferto}
        handleClose={handleCloseEditSanzioneReferto}
        triggerFunction={updateSanzioneReferto}
        gara={editSanzioneReferto}
      />
    </Grid>
  )
}

export default Provvedimenti
